import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  clearStoredIdToken,
  getStoredIdToken,
  persistIdToken,
} from "../lib/tokenUtils";
import { clearActiveCompanyId } from "../lib/companyContext";
import { clearStoredCaseNumber } from "./CaseContext";
import { toast } from "react-toastify";
import { auth } from "../lib/firebase";
import {
  firebaseSignOut,
  getFreshIdToken,
  signInWithGooglePopupSmart,
  exchangeSsoSession,
} from "../lib/firebaseAuth";
const AuthContext = createContext(null);

let ssoExchangePromise = null;

const buildFirebaseUser = (firebaseUser) => {
  if (!firebaseUser) return null;
  return {
    name: firebaseUser.displayName || firebaseUser.email || "User",
    username: firebaseUser.email || firebaseUser.uid,
    uid: firebaseUser.uid,
    provider: "google",
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authSource, setAuthSource] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [ssoChecked, setSsoChecked] = useState(false);

  useEffect(() => {
    if (auth.currentUser) {
      setSsoChecked(true);
      return;
    }
    if (!ssoExchangePromise) {
      ssoExchangePromise = exchangeSsoSession().catch(() => null);
    }
    let cancelled = false;
    ssoExchangePromise.finally(() => {
      if (!cancelled) setSsoChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseReady(true);
      setFirebaseUser(fbUser || null);

      if (!fbUser) {
        if (authSource === "firebase") {
          setUser(null);
          setAuthSource(null);
        }
        return;
      }

      try {
        const token = await getFreshIdToken(false);
        if (token) {
          persistIdToken(token);
        }
      } catch {
        // Ignore token refresh errors
      }

      if (fbUser?.email) {
        localStorage.setItem("lastUpn", fbUser.email);
      }

      setAuthSource("firebase");
      setUser(buildFirebaseUser(fbUser));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [authSource]);

  useEffect(() => {
    let isMounted = true;
    const syncAccount = async () => {
      if (!firebaseReady || !ssoChecked) return;
      if (firebaseUser) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      const justLoggedOut = sessionStorage.getItem("justLoggedOut");
      if (justLoggedOut) {
        sessionStorage.removeItem("justLoggedOut");
        if (!isMounted) return;
        setUser(null);
        clearStoredIdToken();
        setLoading(false);
        return;
      }

      // Check the token this app persisted locally on a previous login
      const sharedToken = getStoredIdToken();
      if (sharedToken) {
        try {
          const decoded = JSON.parse(atob(sharedToken.split(".")[1]));
          const email = decoded.email || null;
          const name = decoded.name || email || "User";
          if (isMounted) {
            setUser({ name, username: email, provider: "sso" });
            setAuthSource("sso");
            setLoading(false);
          }
          return;
        } catch {
          // Malformed token — fall through to unauthenticated
        }
      }

      if (!isMounted) return;
      setUser(null);
      clearStoredIdToken();
      setLoading(false);
    };
    syncAccount();
    return () => {
      isMounted = false;
    };
  }, [firebaseReady, firebaseUser, ssoChecked]);

  const signInWithGoogle = async (loginHint) => {
    try {
      setLoading(true);
      await signInWithGooglePopupSmart(loginHint);
      toast.success("Signed in successfully");
    } catch (error) {
      console.error("Google login failed", error);
      toast.error("Unable to sign in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      localStorage.removeItem("lastUpn");
      sessionStorage.setItem("justLoggedOut", "1");
      if (authSource === "firebase") {
        await firebaseSignOut();
      }
      clearStoredIdToken();
      clearActiveCompanyId();
      clearStoredCaseNumber();
      setUser(null);
      setAuthSource(null);
    } catch (error) {
      console.error("Logout failed", error);
      toast.error("Unable to logout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      loading,
      signInWithGoogle,
      signOut
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
